/**
 * AnimationCanvasRenderer - Shared rendering logic for animated field.
 * Extracts common JSX for field, routes, ghost trail, and players.
 *
 * Uses unified block architecture for WYSIWYG consistency:
 * - AnimatedPlayersOverlay for current player positions (SVG circles)
 * - GhostPlayersOverlay for ghost trail at start positions (SVG circles)
 */

import { FootballField } from '../field/FootballField'
import { AnimatedRoute } from '@/components/canvas-renderer'
import { AnimatedPlayersOverlay } from './AnimatedPlayersOverlay'
import { GhostPlayersOverlay } from './GhostPlayersOverlay'
import type { Drawing } from '@/types'
import type { AnimationState } from '@/types/Animation.types'
import type { FieldCoordinateSystem } from '@/utils'

type AnimationCanvasRendererProps = {
	/** Drawings to render as animated routes */
	drawings: Drawing[]
	/** Animation state containing player positions and settings */
	animationState: AnimationState
	/** Coordinate system for field rendering */
	coordSystem: FieldCoordinateSystem
	/** Player info map (playerId -> {label, color}) */
	playerInfo: Map<string, { label: string; color: string }>
	/** Route progress map (routeId -> progress 0-1) */
	routeProgress: Map<string, number>
	/** Whether to show animation progress on routes */
	showAnimation: boolean
	/** Hide the dimmed ghost path behind animated routes */
	hideGhostPath?: boolean
}

/**
 * Pure rendering component for animated field.
 * Renders field, routes, ghost trail, and players based on animation state.
 */
export function AnimationCanvasRenderer({
											drawings,
											animationState,
											coordSystem,
											playerInfo,
											routeProgress,
											showAnimation,
											hideGhostPath = false,
										}: AnimationCanvasRendererProps) {
	// Get SVG dimensions from coordinate system
	const { width, height } = coordSystem.getDimensions()
	const viewBox = { width, height }

	return (
		<>
			<FootballField />

			{/* Routes, ghost trail, and players - all SVG-based */}
			<svg
				className="pointer-events-none absolute inset-0 size-full"
				viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
				preserveAspectRatio="xMidYMid meet"
			>
				{/* Routes */}
				{drawings.map((drawing) => {
					const progress = routeProgress.get(drawing.id) ?? 0
					return (
						<AnimatedRoute
							key={drawing.id}
							drawing={drawing}
							coordSystem={coordSystem}
							progress={progress}
							showProgress={showAnimation}
							hideGhostPath={hideGhostPath}
						/>
					)
				})}

				{/* Ghost trail at start positions */}
				{animationState.showGhostTrail && (
					<GhostPlayersOverlay
						playerStates={animationState.playerStates}
						coordSystem={coordSystem}
						playerInfo={playerInfo}
						viewBox={viewBox}
					/>
				)}

				{/* Players at current animated positions */}
				<AnimatedPlayersOverlay
					playerStates={animationState.playerStates}
					coordSystem={coordSystem}
					playerInfo={playerInfo}
					viewBox={viewBox}
				/>
			</svg>
		</>
	)
}
